import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Store, Handshake, Mail, Phone, Instagram, MapPin } from "lucide-react";
import { useSEO } from "@/hooks/useSEO";
import { Reveal } from "@/components/shared/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CONTACT_INFO, ENQUIRY_TYPES } from "@/data/siteData";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Contact() {
  useSEO(
    "Contact | The Market Place Jaipur",
    "Start a conversation with The Market Place, Jaipur — whether you want to participate in a market, explore a collaboration, or simply know more."
  );
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    name: "", email: "", phone: "", enquiry_type: params.get("type") || "", message: "",
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.enquiry_type || !form.message) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thank you — we'll be in touch soon.");
      setForm({ name: "", email: "", phone: "", enquiry_type: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="pt-16 pb-12 px-6 sm:px-8 max-w-3xl mx-auto text-center">
        <Reveal>
          <span className="font-mono text-xs uppercase tracking-[0.25em] text-[#D81B60]">Contact</span>
          <h1 className="font-extrabold tracking-tight text-4xl sm:text-5xl lg:text-6xl mt-4 leading-tight">Start a Conversation.</h1>
          <p className="mt-6 text-lg text-gray-600">
            Whether you want to participate in a market, explore a collaboration or simply know more about The
            Market Place, you can reach us here.
          </p>
        </Reveal>
      </section>

      {/* For Businesses / Partnerships / General */}
      <section className="pb-16 px-6 sm:px-8 max-w-6xl mx-auto grid sm:grid-cols-3 gap-6">
        <Reveal>
          <div className="rounded-2xl border border-gray-200 p-7 h-full">
            <div className="w-12 h-12 rounded-2xl bg-[#D81B60]/10 flex items-center justify-center mb-4">
              <Store size={22} className="text-[#D81B60]" strokeWidth={1.75} />
            </div>
            <h3 className="font-bold text-lg mb-2">For Businesses</h3>
            <p className="text-sm text-gray-500 mb-4">Want to participate in an upcoming edition?</p>
            <Link to="/book-a-stall" data-testid="contact-apply-stall-link" className="text-sm font-semibold text-[#D81B60]">Book a Stall →</Link>
            <p className="text-sm text-gray-600 mt-4">{CONTACT_INFO.phone}</p>
            <p className="text-sm text-gray-600">{CONTACT_INFO.email}</p>
          </div>
        </Reveal>
        <Reveal delay={0.06}>
          <div className="rounded-2xl border border-gray-200 p-7 h-full">
            <div className="w-12 h-12 rounded-2xl bg-[#3F51B5]/10 flex items-center justify-center mb-4">
              <Handshake size={22} className="text-[#3F51B5]" strokeWidth={1.75} />
            </div>
            <h3 className="font-bold text-lg mb-2">For Partnerships</h3>
            <p className="text-sm text-gray-500 mb-4">Sponsorships, brand collaborations, venue partnerships and institutional opportunities.</p>
            <p className="text-sm text-gray-600">{CONTACT_INFO.phone}</p>
            <p className="text-sm text-gray-600">{CONTACT_INFO.email}</p>
          </div>
        </Reveal>
        <Reveal delay={0.12}>
          <div className="rounded-2xl border border-gray-200 p-7 h-full">
            <div className="w-12 h-12 rounded-2xl bg-[#1F6F50]/10 flex items-center justify-center mb-4">
              <Mail size={22} className="text-[#1F6F50]" strokeWidth={1.75} />
            </div>
            <h3 className="font-bold text-lg mb-2">General Enquiries</h3>
            <p className="text-sm text-gray-500 mb-4">For all other enquiries, reach out directly.</p>
            <p className="text-sm text-gray-600">{CONTACT_INFO.phone}</p>
            <p className="text-sm text-gray-600">{CONTACT_INFO.email}</p>
          </div>
        </Reveal>
      </section>

      <section className="pb-16 px-6 sm:px-8 max-w-5xl mx-auto grid md:grid-cols-[1fr_1.3fr] gap-12">
        <Reveal>
          <div className="space-y-6">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-gray-400 font-mono mb-2 flex items-center gap-2"><Mail size={14} /> Email</p>
              <a href={`mailto:${CONTACT_INFO.email}`} data-testid="contact-email" className="text-lg text-[#D81B60] font-semibold">{CONTACT_INFO.email}</a>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-gray-400 font-mono mb-2 flex items-center gap-2"><Phone size={14} /> Phone</p>
              <a href={`tel:${CONTACT_INFO.phone}`} data-testid="contact-phone" className="text-lg text-[#1A1A1A] font-semibold">{CONTACT_INFO.phone}</a>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-gray-400 font-mono mb-2 flex items-center gap-2"><Instagram size={14} /> Instagram</p>
              <a href={CONTACT_INFO.instagram} target="_blank" rel="noopener noreferrer" data-testid="contact-instagram" className="text-lg text-[#1A1A1A] font-semibold">{CONTACT_INFO.instagramHandle}</a>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-gray-400 font-mono mb-2 flex items-center gap-2"><MapPin size={14} /> Visit</p>
              <p className="text-lg text-[#1A1A1A] font-semibold">The Market Place Jaipur</p>
              <p className="text-sm text-gray-500">Jaipur, Rajasthan</p>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-5 rounded-3xl bg-[#FDFBF7] border border-gray-100 p-8">
            <div>
              <Label htmlFor="name">Name</Label>
              <Input id="name" data-testid="contact-name-input" value={form.name} onChange={(e) => handleChange("name", e.target.value)} className="mt-1.5 focus-visible:ring-[#D81B60]" />
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <Label htmlFor="phone">Phone Number</Label>
                <Input id="phone" data-testid="contact-phone-input" value={form.phone} onChange={(e) => handleChange("phone", e.target.value)} className="mt-1.5 focus-visible:ring-[#D81B60]" />
              </div>
              <div>
                <Label htmlFor="email">Email Address</Label>
                <Input id="email" type="email" data-testid="contact-email-input" value={form.email} onChange={(e) => handleChange("email", e.target.value)} className="mt-1.5 focus-visible:ring-[#D81B60]" />
              </div>
            </div>
            <div>
              <Label>I am interested in</Label>
              <Select value={form.enquiry_type} onValueChange={(v) => handleChange("enquiry_type", v)}>
                <SelectTrigger data-testid="contact-enquiry-type-select" className="mt-1.5 focus:ring-[#D81B60]">
                  <SelectValue placeholder="Select one" />
                </SelectTrigger>
                <SelectContent>
                  {ENQUIRY_TYPES.map((t) => (
                    <SelectItem key={t} value={t} data-testid={`enquiry-type-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="message">Message</Label>
              <Textarea id="message" data-testid="contact-message-input" rows={5} value={form.message} onChange={(e) => handleChange("message", e.target.value)} className="mt-1.5 focus-visible:ring-[#D81B60]" />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="contact-submit-button"
              className="w-full rounded-full bg-[#D81B60] text-white py-4 text-sm font-semibold hover:bg-[#AD144B] transition-colors duration-300 disabled:opacity-60"
            >
              {loading ? "Sending..." : "Send Enquiry"}
            </button>
          </form>
        </Reveal>
      </section>

      <section className="pb-16 px-6 sm:px-8 text-center">
        <p className="font-extrabold text-2xl sm:text-3xl text-[#D81B60]">To Jaipur. From Jaipur.</p>
        <p className="mt-2 text-gray-500">Built around the businesses, people and ideas that continue to make this city worth discovering.</p>
      </section>
    </div>
  );
}
